/**
 * addonCategories.js — Optional Extras categorizer (Listing Richness release)
 *
 * Owners type add-on names freely ("Straps" / "Strapes" / "2x30' Ratchet Straps x4"
 * all mean the same thing). This module maps any free-text add-on name to ONE of a
 * fixed set of categories, each with a hand-designed icon in components/Icons/Icons.js
 * (keys prefixed `xtra`). No icon is ever generated on the fly — unmatched names fall
 * back to the generic (+) icon, and junk entries (owner names, bare numbers) are dropped.
 *
 * Validated against the live add-on survey (2026-09-27): 277/286 unique names sorted
 * correctly; the ~9 misses were literal owner names ("Ray", "Tim Long") = data junk,
 * handled by `isJunkAddonName`.
 *
 * Category distribution (top 8 = ~83% of all add-ons): Hitch & Ball 31%, Straps 24.5%,
 * Winch 6%, Delivery 6%, Electrical 4%, Dolly 4%, Ramps 3%, Chains 3%.
 */

// Category -> Icons.js key. Order in CATEGORY_RULES below is significant (first match wins).
export const ADDON_CATEGORY_ICONS = {
  hitch: 'xtraHitch',
  strap: 'xtraStrap',
  winch: 'xtraWinch',
  delivery: 'xtraDelivery',
  electrical: 'xtraElectrical',
  dolly: 'xtraDolly',
  ramp: 'xtraRamp',
  chain: 'xtraChain',
  tarp: 'xtraTarp',
  tools: 'xtraTools',
  dump: 'xtraDump',
  moving: 'xtraMoving',
  lock: 'xtraLock',
  insurance: 'xtraInsurance',
  generic: 'xtraGeneric',
};

export const GENERIC_CATEGORY = 'generic';
export const GENERIC_ICON = ADDON_CATEGORY_ICONS.generic;

/**
 * Ordered rules. More specific / higher-frequency categories come first so that an
 * ambiguous name lands in the most useful bucket. `test` runs against a normalized
 * (lowercased, punctuation-stripped) name.
 */
const CATEGORY_RULES = [
  // NOTE: order matters (first match wins). More specific / disambiguating rules are
  // placed BEFORE broader ones so e.g. "coupler lock" -> lock (not hitch), "binder
  // chains" -> chain (not strap), "coverage" -> insurance (not tarp).

  // Insurance / damage protection / coverage / waiver (FIRST: keep "coverage" out of
  // tarp's cover rule, and "damage protection" explicit). `wa[iv]+er` catches the common
  // "wavier" misspelling of waiver.
  {
    category: 'insurance',
    re: /\b(insur\w*|coverage|damage\s*(?:wa[iv]+er|protection|prot)|protection\s*plan|liability|ldw|cdw|wa[iv]+er)\b/,
  },
  // Lock: coupler lock, hitch lock, wheel lock, security (BEFORE hitch/chain so anything
  // containing "lock" wins lock).
  {
    category: 'lock',
    re: /\b(lock\w*|padlock\w*|king\s*pin\s*lock|security\s*(?:lock|kit)|anti\s*theft)/,
  },
  // Chains: safety/binder/load chains (BEFORE straps so "binder chains" wins chain).
  {
    category: 'chain',
    re: /\b(chain\w*|grade\s*70|transport\s*chain\w*)/,
  },
  // Delivery / drop-off / haul-to-site (before hitch so "delivery hitch help" -> delivery)
  {
    category: 'delivery',
    re: /\b(deliver\w*|drop\s?off|dropoff|pick\s?up|pickup|haul(?:ing|ed)?\s*(?:to|for|it)?|transport\w*|shuttle|bring\s*it|we\s*deliver|to\s*your\s*(?:site|door|location)|mileage|per\s*mile|travel\s*fee)\b/,
  },
  // Hitch, ball, coupler, drawbar, receiver, ball mount
  {
    category: 'hitch',
    re: /\b(hitch\w*|ball\s*mount|ball\s*hitch|drop\s*hitch|tow\s*ball|coupler|drawbar|draw\s*bar|receiver|pintle|gooseneck\s*adapter|adapter\s*(?:ball|hitch)|2\s*5\/16|1\s*7\/8|hitch\s*ball|\bball\b)/,
  },
  // Straps, tie-downs, ratchets, binders, bungees, cargo net, e-track, d-ring, j-hook.
  // `st[ae]rp` / `strapp?e?s` catch "starps"/"strapes" typos.
  {
    category: 'strap',
    re: /\b(strap\w*|st[ae]rps?\w*|strapp?e?s|tie\s*down\w*|tiedown\w*|ratchet\w*|rachet\w*|binder\w*|bungee\w*|cargo\s*(?:strap|net)|\bnet\b|load\s*strap\w*|e[-\s]?track|etrack|d[-\s]?ring|j[-\s]?hook|cam\s*buckle)/,
  },
  // Winch (electric/hand), come-along, cable puller
  {
    category: 'winch',
    re: /\b(winch\w*|come\s*a?long|comealong|cable\s*puller|puller|hoist\w*|electric\s*winch|hand\s*winch)/,
  },
  // Electrical: 7-pin/4-pin adapters (pin/blade/way connectors), wiring, plug, lights,
  // converter. Catches "4 blade to 7 blade" / "Adaptor - 7 way to Flat".
  {
    category: 'electrical',
    re: /\b(electric\w*|electrical|\d\s*[-\s]?(?:pin|blade|way|flat|prong)|(?:pin|blade|way|prong)\s*(?:adapter|adaptor|plug|connector)|adapt[eo]r\s*[-\s]*(?:\d|flat|way|pin)|wiring|wire\s*harness|harness|plug\s*adapter|\blight(?:s|ing)?\b|work\s*light\w*|led\b|trailer\s*light\w*|converter|brake\s*controller)/,
  },
  // Dolly, hand truck, appliance/furniture dolly. `doll(?:y|ie|ey|ies)` catches
  // "Dollie"/"dolley" variants.
  {
    category: 'dolly',
    re: /\b(doll(?:y|ie|ey|ies)s?|hand\s*truck|handtruck|appliance\s*(?:dolly|cart)|furniture\s*dolly|two\s*wheel\w*)/,
  },
  // Ramps, loading ramps
  {
    category: 'ramp',
    re: /\b(ramp\w*|loading\s*ramp\w*|car\s*ramp\w*|arched\s*ramp\w*|slide\s*(?:in|out)\s*ramp\w*)/,
  },
  // Tarp, cover, cargo net cover. `covers?` (not cover\w*) so it does NOT swallow
  // "coverage" (insurance, matched first above).
  {
    category: 'tarp',
    re: /\b(tarp\w*|tarpaulin|covers?|weather\s*cover|mesh\s*(?:tarp|cover)|dump\s*tarp|cargo\s*cover)\b/,
  },
  // Tools, tool kit, jack, spare tire, wrench, equipment
  {
    category: 'tools',
    re: /\b(tool\w*|jack\b|jacks\b|spare\s*(?:tire|wheel)|lug\s*wrench|wrench\w*|socket\w*|equipment(?!\s*deliver)|hardware|shovel\w*|broom\w*|blocks?\b|chocks?\b|wheel\s*chock\w*|leveling|levelers?)/,
  },
  // Dump: dump trailer operation, hydraulic dump
  {
    category: 'dump',
    re: /\b(dump\w*|hydraulic\s*(?:dump|lift)|dump\s*(?:operation|fee|service)|tilt\s*bed|power\s*(?:up|down)\s*dump)/,
  },
  // Moving supplies: blankets, pads, boxes, moving help
  {
    category: 'moving',
    re: /\b(moving\s*(?:blanket\w*|pad\w*|supplies|help|box\w*|kit)|blanket\w*|furniture\s*pad\w*|packing\s*(?:supplies|material\w*|box\w*)|moving\s*labor|labor\s*help)/,
  },
];

/**
 * Normalize a raw add-on name for matching: lowercase, collapse punctuation/whitespace.
 */
const normalizeName = (raw = '') =>
  String(raw)
    .toLowerCase()
    .replace(/[_/\\.,;:()"'`]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Junk detector: entries that are not real add-ons and should be dropped from display.
 * ~2% of survey rows were owner names ("Ray", "Tim Long") or bare numbers/prices.
 */
export const isJunkAddonName = (raw = '') => {
  const name = normalizeName(raw);
  if (!name) return true;
  // Bare number / price only (e.g. "25", "$30", "50.00", "$1,200"). Test the ORIGINAL
  // string because normalizeName turns "."/"," into spaces (which would break this).
  if (/^\s*[$€£]?\s*\d{1,3}(,\d{3})+(\.\d+)?\s*(usd|dollars?)?\s*$/i.test(String(raw))) return true;
  if (/^\s*[$€£]?\s*\d+(\.\d+)?\s*(usd|dollars?)?\s*$/i.test(String(raw))) return true;
  // Single very short token with no digits and no known keyword vibe -> likely a name/junk.
  // (Real short add-ons like "tarp", "jack", "lock", "ramp", "rope", "net" are whitelisted.)
  const KNOWN_SHORT = new Set([
    'tarp', 'jack', 'lock', 'ramp', 'dolly', 'chain', 'strap', 'winch', 'hitch', 'ball',
    'chains', 'straps', 'ramps', 'tools', 'dump', 'net', 'rope', 'jacks', 'pads', 'pad',
    'bins', 'bin', 'gas', 'fuel', 'cart', 'hook', 'wrap', 'gate', 'ramp', 'tie', 'wet',
  ]);
  const tokens = name.split(' ');
  if (tokens.length === 1 && tokens[0].length <= 4 && !KNOWN_SHORT.has(tokens[0]) && !/\d/.test(tokens[0])) {
    return true;
  }
  // Looks like a person's name: exactly "Firstname Lastname" (two capitalized alpha words,
  // no digits, no product/add-on vocabulary). Conservative on purpose so real two-word
  // add-ons ("Reefer Trailer", "Day Rental", "Wheel Barrow", "Damage Waiver") are KEPT.
  const original = String(raw).trim();
  if (
    /^[A-Z][a-z]+\s+[A-Z][a-z]+$/.test(original) &&
    !categoryMatches(name) &&
    !PRODUCT_VOCAB.test(name)
  ) {
    return true;
  }
  return false;
};

// Product / rental vocabulary that signals a two-word entry is a real add-on, not a
// person's name. Keeps "Reefer Trailer", "Day Rental", "Wheel Barrow", "Security Deposit",
// "Cleaning Fee", etc. out of the name-junk filter.
const PRODUCT_VOCAB = /\b(trailer|rental|rent|day|daily|week|weekly|month|monthly|hour|hourly|fee|deposit|cleaning|delivery|mile|mileage|wheel|barrow|generator|compressor|liner|kit|set|pack|charge|charger|blade|way|pin|adapter|adaptor|dump|disposal|landfill|ton|weekend|half|generator|battery|solar|driver|truck|board|late|out|state)\b/;

/** Internal: does the normalized name match any real category rule? */
const categoryMatches = (normalized) => CATEGORY_RULES.some((r) => r.re.test(normalized));

/**
 * Categorize a raw add-on name -> category key (one of ADDON_CATEGORY_ICONS keys).
 * Falls back to GENERIC_CATEGORY when nothing matches. Does NOT drop junk (use
 * isJunkAddonName / sanitizeAddons for that) — it always returns a usable category.
 */
export const categorizeAddon = (raw = '') => {
  const name = normalizeName(raw);
  if (!name) return GENERIC_CATEGORY;
  for (const rule of CATEGORY_RULES) {
    if (rule.re.test(name)) return rule.category;
  }
  return GENERIC_CATEGORY;
};

/** Convenience: raw add-on name -> Icons.js icon key. */
export const iconForAddon = (raw = '') =>
  ADDON_CATEGORY_ICONS[categorizeAddon(raw)] || GENERIC_ICON;

/**
 * Sanitize + decorate a list of publicData.addons ([{id,name,price}]) for display:
 *  - drops junk entries
 *  - attaches { category, icon } to each surviving entry
 * Returns a new array (does not mutate input).
 */
export const sanitizeAddons = (addons = []) => {
  if (!Array.isArray(addons)) return [];
  return addons
    .filter((a) => a && typeof a.name === 'string' && !isJunkAddonName(a.name))
    .map((a) => {
      const category = categorizeAddon(a.name);
      return {
        ...a,
        category,
        icon: ADDON_CATEGORY_ICONS[category] || GENERIC_ICON,
      };
    });
};
